import { useState, type FormEvent } from "react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errorMessage } from "@/lib/api";
import { resetUserMfa, updateMfaPolicy, type Korisnik } from "@/lib/korisnici";

export function UserMfaSettings({ user, isSelf, onUpdated }: {
  user: Korisnik;
  isSelf: boolean;
  onUpdated: (user: Korisnik) => void;
}) {
  const [required, setRequired] = useState(user.mfa_required);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const protectedSelf = isSelf && user.mfa_required;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submitter = (event.nativeEvent as SubmitEvent).submitter;
    const reset = submitter?.getAttribute("value") === "reset";
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const updated = reset
        ? await resetUserMfa(user.id, !required, password)
        : await updateMfaPolicy(user.id, required, password);
      setRequired(updated.mfa_required);
      onUpdated(updated);
      setMessage(reset ? "Dvofaktorska prijava je isključena. Korisnik se može prijaviti lozinkom." : "Obveza dvofaktorske prijave je spremljena.");
    } catch (caught) {
      setError(errorMessage(caught, "Promjena dvofaktorske prijave nije uspjela."));
    } finally {
      setPassword("");
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="user-mfa-title" className="avm-surface rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-4 sm:p-6">
      <h2 id="user-mfa-title" className="text-lg font-semibold">Dvofaktorska prijava</h2>
      <p className="mt-2 text-sm">{user.full_name}: {user.mfa_confirmed ? "2FA je uključena." : "2FA nije postavljena."}</p>
      <form onSubmit={submit} className="mt-4 flex max-w-xl flex-col gap-4">
        {error && <Alert variant="error">{error}</Alert>}
        {message && <p role="status">{message}</p>}
        <label className="flex min-h-11 items-center gap-3">
          <input type="checkbox" role="switch" className="size-5" checked={required} disabled={busy || protectedSelf} onChange={(event) => setRequired(event.target.checked)} />
          <span>Obvezna</span>
        </label>
        <p className="text-sm text-muted-foreground">Ako je obvezna, korisnik bez 2FA pri sljedećoj prijavi mora postaviti aplikaciju za autentifikaciju prije ulaska.</p>
        {protectedSelf && <p className="text-sm">Ne možete sebi isključiti obvezu dvofaktorske prijave.</p>}
        <Label htmlFor="admin-mfa-password">Potvrdite svojom lozinkom superadmina</Label>
        <Input id="admin-mfa-password" type="password" autoComplete="current-password" required value={password} disabled={busy} onChange={(event) => setPassword(event.target.value)} />
        <div className="flex flex-wrap gap-3">
          <Button type="submit" value="policy" disabled={busy || !password || required === user.mfa_required}>Spremi obvezu</Button>
          <Button type="submit" value="reset" variant="destructive" disabled={busy || !password || required}>Isključi 2FA korisniku</Button>
        </div>
        <p className="text-sm text-muted-foreground">Isključivanje briše ključ i sve rezervne kodove. Za obveznu 2FA prvo ugasite prekidač; gumb zatim sprema obje promjene zajedno. Korisnik može poslije sam ponovno uključiti 2FA.</p>
      </form>
    </section>
  );
}
